// Tab-scoped Cognito token storage, after the portal's lib/cognitoTabStorage.ts.
// sessionStorage is the live store, so each tab keeps its own session. Writes are mirrored to
// localStorage only to seed a newly opened tab; a tab never adopts a seed over its own keys.
// Falls back to memory when storage is blocked (private mode, sandboxed previews).
const COGNITO_PREFIX = 'CognitoIdentityServiceProvider.';

function probe(get: () => Storage): Storage | null {
  try {
    const s = get();
    const k = '__cognito_probe__';
    s.setItem(k, '1');
    s.removeItem(k);
    return s;
  } catch {
    return null;
  }
}

function memoryStorage(): Storage {
  const m = new Map<string, string>();
  return {
    get length() { return m.size; },
    clear: () => m.clear(),
    getItem: (k: string) => (m.has(k) ? m.get(k)! : null),
    key: (i: number) => Array.from(m.keys())[i] ?? null,
    removeItem: (k: string) => void m.delete(k),
    setItem: (k: string, v: string) => void m.set(k, String(v)),
  };
}

function cognitoKeys(store: Storage): string[] {
  const keys: string[] = [];
  try {
    for (let i = 0; i < store.length; i++) {
      const k = store.key(i);
      if (k && k.startsWith(COGNITO_PREFIX)) keys.push(k);
    }
  } catch { /* enumeration blocked: treat as empty */ }
  return keys;
}

export interface CognitoTabStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
  clear(): void;
}

/** Build once per tab; the user pool singleton routes every SDK read and write through it. */
export function makeCognitoTabStorage(): CognitoTabStorage {
  const session: Storage = (typeof window !== 'undefined' && probe(() => window.sessionStorage)) || memoryStorage();
  const local: Storage | null = typeof window !== 'undefined' ? probe(() => window.localStorage) : null;

  if (local) {
    for (const k of cognitoKeys(local)) {
      if (session.getItem(k) !== null) continue;
      const v = local.getItem(k);
      if (v !== null) {
        try { session.setItem(k, v); } catch { /* full or blocked: the live session still works */ }
      }
    }
  }

  const mirror = (fn: (s: Storage) => void) => {
    if (!local) return;
    try { fn(local); } catch { /* mirror failed: the live session is unaffected */ }
  };

  return {
    getItem(key) {
      try { return session.getItem(key); } catch { return null; }
    },
    setItem(key, value) {
      try { session.setItem(key, value); } catch { /* blocked: the SDK re-authenticates */ }
      mirror((s) => s.setItem(key, value));
    },
    removeItem(key) {
      let owned: string | null = null;
      try { owned = session.getItem(key); session.removeItem(key); } catch { /* ignore */ }
      // Only remove the seed if it is still this tab's value, not a newer login's.
      if (owned !== null) mirror((s) => { if (s.getItem(key) === owned) s.removeItem(key); });
    },
    clear() {
      const owned: Array<[string, string]> = [];
      for (const k of cognitoKeys(session)) {
        try {
          const v = session.getItem(k);
          if (v !== null) owned.push([k, v]);
          session.removeItem(k);
        } catch { /* ignore */ }
      }
      mirror((s) => { for (const [k, v] of owned) if (s.getItem(k) === v) s.removeItem(k); });
    },
  };
}
